(ns hai.ui.components.collection.views.table
  (:require
   ["react" :as react]
   [hai.ui.components :as c]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.pagination :as pagination]
   [hai.ui.components.collection.views.utils :as view.utils]
   [hai.ui.components.dropdown-menu :as dropdown-menu]
   [hai.ui.components.form.core :as form]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s]]
   [lambdaisland.ornament :as o]))

(o/defstyled table-cell-inner :div
  :inline-flex :items-center :gap-2
  :w-100% :h-100%
  :cursor-default
  ([& children]
   (into [:<>] children)))

(defn table-cell
  [& children]
  [c/TableCell
   (into [table-cell-inner]
         children)])

(defn title-render
  [{:keys [title on-click]}]
  [table-cell
   [c/Link
    {:on-click on-click
     :style {:cursor "pointer"}
     :href "#"
     :variant "ghost"}
    title]])

(defn label
  [{:keys [items text xform]}]
  (if (seq items)
    (let [truncate-to 3
          truncate-shared-with? (< truncate-to (count items))
          shared-with (take truncate-to items)]
      (cond-> (into [:span]
                    (if xform
                      (comp xform (interpose ", "))
                      (interpose ", "))
                    shared-with)
        truncate-shared-with? (conj "...")))
    text))

(o/defstyled more-menu-cell* :div
  :flex :flex-col :items-center :justify-center
  :h-100% :w-100%
  [:button
   :flex :items-center :justify-center
   {:border "none"
    :outline "none"
    :background "transparent"
    :cursor "pointer"}]
  [icons/filled-more_vert
   {:fill c/--gray-12}]
  ([actions]
   [:<>
    [c/DropdownMenuRoot {:modal false}
     [c/DropdownMenuTrigger
      [:button
       [icons/filled-more_vert]]]
     (into [c/DropdownMenuContent]
           (map (fn [[caption action icon]]
                  [c/DropdownMenuItem {:on-click action}
                   [dropdown-menu/menu-item-content icon caption]]))
           actions)]]))

(defn more-menu-cell
  [actions]
  [c/TableCell
   {:style {:width "fit-content"
            :padding "0"}}
   [more-menu-cell* actions]])

(defn modal-cell*
  [{:keys [text on-close on-open-change]} children]
  (let [[open on-open] (react/useState false)]
    [table-cell
     [c/DialogRoot
      {:open open
       :onOpenChange (fn [open?]
                       (on-open open?)
                       (when on-open-change
                         (on-open-change open?))
                       (when (and (not open?)
                                  on-close)
                         (on-close)))}
      [c/DialogTrigger
       ;; TODO using a custom component throws and error as radix tries to focus the trigger on
       ;; model close and standard reagent components do not compose as child components out of the
       ;; box
       [c/link-button
        {:on-click (fn [_] (on-open true))}
        text]]
      (into [c/DialogContent [c/dialog-close-x]]
            children)]]))

(defn modal-cell
  [props & children]
  [:f> modal-cell* props children])

(defn render-cell
  "A component to render an individual cell with either a custom `:render` component or simply the `:attr`"
  [table row {:table.column/keys [attr render render-with-collection] :as _column}]
  (cond
    render-with-collection [render-with-collection table row]
    render [render row]
    attr [table-cell (get row attr)]
    :else [table-cell [c/Text {:color "red"} [c/Code "No `:attr` or `:render` on column definition"]]]))

(defn header
  "A straightforward header component that iterates over the column definitions and renders the
  `:header` attribute."
  [{:as table
    :table/keys [columns]}]
  [c/TableHeader
   (cond-> [c/TableRow]
     (bulk-actions/show? table) (conj [bulk-actions/header-cell table])
     :always (into (map (fn [{:table.column/keys [width] :as column}]
                          [c/TableColumnHeaderCell
                           ;; A width on the header cell sizes the whole column; pin
                           ;; min/max too so it holds under auto table layout.
                           (cond-> {}
                             width (assoc :style {:width width
                                                  :min-width width
                                                  :max-width width}))
                           (if (:table/paginate? table)
                             [pagination/header-cell table column]
                             [:span (:table.column/header column)])]))
                   columns))
   ;; Gated behind the "Show filters" setting, whether the row is the built-in
   ;; one or a custom `:filters/render` bar. A `:sibling` filters placement
   ;; mounts it next to the table root instead — see `view`.
   (when (and (not= :sibling (:filters/placement table))
              (-> table :table/form :form/current-value :table/settings :show-filters-table?))
     [filters/component table])])

(o/defstyled loading-row* :div
  :flex :justify-center
  {:padding "17px"}
  ([]
   [:em "Loading..."]))

(defn loading-row
  [table]
  (when @(<s [:table/resolving? table])
    [:tr [:td {:colSpan 20}
          [loading-row*]]]))

(o/defstyled error-row* :div
  :flex :justify-center
  :p-5
  ([]
   [c/Text {:color "red"}
    [c/Code "An error occurred while loading table"]]))

(defn error-row
  [table]
  (when @(<s [:collection.query/error table])
    [:tr [:td {:colSpan 20}
          [error-row*]]]))

(o/defstyled no-items-row* :div
  :flex :justify-center
  :p-5
  ([{:table/keys [no-items] :as table}]
   (if no-items
     [no-items table]
     [c/Text [:em "No items found"]])))

(defn no-items-row
  [table {:form.field/keys [current-value] :as _rows-field}]
  (when (not (or (seq current-value)
                 @(<s [:table/resolving? table])
                 @(<s [:collection.query/error table])))
    [:tr [:td {:colSpan 20}
          [no-items-row* table]]]))

(defn row-render
  [{:table/keys [columns] :as table}
   {:form/keys [initial-value] :as row}]
  (cond-> [c/TableRow {:class (view.utils/classes row)}]
    (bulk-actions/show? table) (conj [bulk-actions/select-checkbox table initial-value])
    :always (into (map (partial view.utils/render-cell-or-field render-cell table row))
                  columns)))

(defn view
  [{:table/keys [pagination-position]
    {{:table/keys [rows]} :form/fields} :table/form :as table}]
  (let [row-render (partial row-render table)
        rows (assoc rows :form/render row-render)
        ;; nil-safe: absent position == :bottom (prior behavior), so tables
        ;; that never set this keep their bottom controls regardless of whether
        ;; the malli default gets injected on this path.
        position (or pagination-position :bottom)
        top? (#{:top :both} position)
        bottom? (#{:bottom :both} position)]
    [:<>
     ;; `:sibling` filters placement: the filter UI is page chrome living
     ;; beside the table, not a header row inside it (see `header`).
     (when (and (= :sibling (:filters/placement table))
                (-> table :table/form :form/current-value :table/settings :show-filters-table?))
       [filters/component table])
     ;; Pagination controls sit outside the table as plain divs (see
     ;; `pagination/controls`), above and/or below per `pagination-position`.
     (when top?
       [pagination/controls table])
     [c/TableRoot {:variant "surface"}
      [header table]
      [c/TableBody
       ;; The `rows` var in scope in `form-render` is the form field, not the initial
       ;; rows data.
       [form/collection rows]
       [no-items-row table rows]
       [loading-row table]
       [error-row table]]]
     (when bottom?
       [pagination/controls table])]))
